/**
 * Ruta de firmas de un Estado de Pago / Orden de Compra (SOU-481).
 *
 * El orden del flujo pasa a ser Emitido -> Revisado -> Aprobado -> Enviado, pero las
 * ordenes anteriores al cambio recorrieron Emitido -> Revisado -> Enviado -> Aprobado.
 * Para no reescribir la historia de un documento ya enviado al cliente, los pasos se
 * ordenan por su FECHA real en vez de por una secuencia fija.
 *
 * Lo consumen el drawer de detalle y la ruta de firmas del PDF, para que no puedan
 * divergir: son la misma informacion en dos soportes distintos.
 */

/** Pasos del flujo de firmas, en el orden canonico nuevo. */
export type OrderStepKey = 'issued' | 'reviewed' | 'approved' | 'sent';

/** Orden del flujo nuevo: se aprueba y despues se envia. */
export const CANONICAL_STEP_ORDER: OrderStepKey[] = ['issued', 'reviewed', 'approved', 'sent'];

/** Orden del flujo anterior al cambio: se enviaba y despues se aprobaba. */
export const LEGACY_STEP_ORDER: OrderStepKey[] = ['issued', 'reviewed', 'sent', 'approved'];

/**
 * Detecta si la orden recorrio el flujo ANTIGUO, es decir, si se envio antes (o en el
 * mismo instante) de aprobarse.
 *
 * Hace falta para desempatar: en los documentos historicos reales es habitual que
 * revision, envio y aprobacion compartan el mismo minuto, y entonces la fecha por si
 * sola no dice cual fue primero. Sin esto, un EP antiguo se reimprimiria con el orden
 * nuevo — que es exactamente la regresion que hay que evitar.
 */
export function usedLegacySignatureFlow(order: any): boolean {
  const shipped = order?.shipperDate ? new Date(order.shipperDate).getTime() : null;
  const approved = order?.approverDate ? new Date(order.approverDate).getTime() : null;

  if (shipped === null || approved === null) {
    // Sin las dos fechas no hay empate posible: las propias fechas ya ordenan.
    return false;
  }

  return shipped <= approved;
}

export interface OrderSignatureStep {
  key: OrderStepKey;
  /** Titulo del paso: "Emitido", "Revisado"... */
  title: string;
  /** Etiqueta de autoria: "Emitido por:", "Revisado por:"... */
  byLabel: string;
  /** Firmante, si el paso ya se ejecuto. */
  partner?: any;
  /** Fecha de ejecucion. Vacia si el paso esta pendiente. */
  date?: string | Date;
}

/**
 * Construye los cuatro pasos de la ruta de firmas a partir de una orden, sin ordenar.
 * Acepta InvoiceOrder o PurchaseOrder: los dos comparten estos campos.
 */
export function buildOrderSignatureSteps(order: any): OrderSignatureStep[] {
  return [
    {key: 'issued', title: 'Emitido', byLabel: 'Emitido por:', partner: order?.transmitter, date: order?.transmitterDate},
    {key: 'reviewed', title: 'Revisado', byLabel: 'Revisado por:', partner: order?.revision, date: order?.revisionDate},
    {key: 'approved', title: 'Aprobado', byLabel: 'Aprobado por:', partner: order?.approver, date: order?.approverDate},
    {key: 'sent', title: 'Enviado', byLabel: 'Enviado por:', partner: order?.shipper, date: order?.shipperDate},
  ];
}

/**
 * Ordena los pasos por su fecha real:
 *
 * - Los ya ejecutados van primero, en orden cronologico.
 * - Los pendientes van despues, en el orden del flujo que corresponda.
 * - A igualdad de fecha desempata el orden del flujo que RECORRIO esa orden: el antiguo
 *   si se envio antes de aprobarse, el nuevo en el resto de casos.
 *
 * El desempate no es un detalle: en los documentos historicos reales revision, envio y
 * aprobacion suelen llevar el mismo minuto, y sin esto se reimprimirian con el orden
 * nuevo — reescribiendo el recorrido de un documento que ya se envio al cliente.
 *
 * No muta el array recibido.
 */
export function sortOrderSignatureSteps(steps: OrderSignatureStep[], usesLegacyFlow = false): OrderSignatureStep[] {
  const tieBreakOrder = usesLegacyFlow ? LEGACY_STEP_ORDER : CANONICAL_STEP_ORDER;
  const stepIndex = (step: OrderSignatureStep) => tieBreakOrder.indexOf(step.key);
  // Los pasos sin fecha se van al final, y entre ellos desempata el orden del flujo.
  const time = (step: OrderSignatureStep) =>
    step.date ? new Date(step.date).getTime() : Number.POSITIVE_INFINITY;

  return [...steps].sort((a, b) => {
    const diff = time(a) - time(b);
    if (diff !== 0 && !Number.isNaN(diff)) return diff;
    return stepIndex(a) - stepIndex(b);
  });
}

/**
 * Pasos ya ejecutados, en orden cronologico. Para la linea de tiempo del drawer, que
 * solo muestra lo que ha ocurrido.
 */
export function getExecutedOrderSignatureSteps(order: any): OrderSignatureStep[] {
  return sortOrderSignatureSteps(
    buildOrderSignatureSteps(order).filter(step => !!step.date),
    usedLegacySignatureFlow(order));
}

/**
 * Los cuatro pasos siempre, en orden. Para la ruta de firmas del PDF, que es una tabla
 * impresa de cuatro columnas y muestra las pendientes en blanco.
 */
export function getAllOrderSignatureSteps(order: any): OrderSignatureStep[] {
  return sortOrderSignatureSteps(buildOrderSignatureSteps(order), usedLegacySignatureFlow(order));
}
